import axios from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as authApi from '@core/api/auth';
import type { User } from '@core/api/user';
import { register as registerBridge } from '@core/utils/authBridge';
import { getTokenExpirationMs } from '@core/utils/jwt';
import { clearTenantSession } from '@core/utils/tenantSession';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function loadStored(): { token: string; user: User } | null {
  try {
    const token = localStorage.getItem('authToken');
    const userJson = localStorage.getItem('user');
    if (!token || !userJson) return null;
    const user = JSON.parse(userJson) as User;
    return { token, user };
  } catch {
    return null;
  }
}

function clearStorage(): void {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  clearTenantSession();
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef<string | null>(null);
  const expirationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExpirationTimer = useCallback(() => {
    if (expirationTimerRef.current !== null) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }
  }, []);

  const clearAuth = useCallback(() => {
    clearExpirationTimer();
    tokenRef.current = null;
    setUser(null);
    clearStorage();
  }, [clearExpirationTimer]);

  const scheduleExpiration = useCallback(
    (token: string) => {
      const expMs = getTokenExpirationMs(token);
      if (expMs === null) return;
      const delay = Math.max(0, expMs - Date.now());
      clearExpirationTimer();
      expirationTimerRef.current = setTimeout(clearAuth, delay);
    },
    [clearAuth, clearExpirationTimer]
  );

  useEffect(() => {
    registerBridge(tokenRef, clearAuth);
    return () => registerBridge({ current: null }, () => {});
  }, [clearAuth]);

  useEffect(() => {
    const init = async () => {
      const stored = loadStored();
      if (!stored) {
        setLoading(false);
        return;
      }
      const { token, user: storedUser } = stored;
      const expMs = getTokenExpirationMs(token);
      if (expMs !== null && expMs <= Date.now()) {
        clearAuth();
        setLoading(false);
        return;
      }
      tokenRef.current = token;
      setUser(storedUser);
      scheduleExpiration(token);
      try {
        const freshUser = await authApi.getCurrentUser();
        setUser(freshUser);
        localStorage.setItem('user', JSON.stringify(freshUser));
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          clearAuth();
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [clearAuth, scheduleExpiration]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      setLoading(true);
      try {
        const { token, user: newUser } = await authApi.login(
          identifier,
          password
        );
        tokenRef.current = token;
        setUser(newUser);
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(newUser));
        scheduleExpiration(token);
      } finally {
        setLoading(false);
      }
    },
    [scheduleExpiration]
  );

  const logout = useCallback(async () => {
    window.dispatchEvent(new CustomEvent('logout'));
    await new Promise((r) => setTimeout(r, 100));
    try {
      await authApi.logout();
    } catch {
      // continue
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  const updateUser = useCallback((user: User) => {
    setUser(user);
    localStorage.setItem('user', JSON.stringify(user));
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
